import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Toaster, toast } from "sonner";
import { RegisterLayout, Input, Textarea, SubmitButton } from "../components/RegisterLayout";
import { TagPicker } from "../components/TagPicker";
import { SecurityPulseGate } from "../components/SecurityPulseGate";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { validatePan, validateGstin, crossCheckPanGstin } from "../lib/gst";

export default function SellerRegister() {
  const { registerSeller, api } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [pulseOpen, setPulseOpen] = useState(false);
  const [form, setForm] = useState({
    business_name: "", contact_person: "", email: "", phone: "", gst_number: "", pan: "",
    address: "", materials_supplied: "", supply_capacity: "", years_in_business: "", docs_note: "", password: "",
  });
  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (name === "gst_number") setGstErr("");
    if (name === "pan") setPanErr("");
  };
  const [gstErr, setGstErr] = useState("");
  const [panErr, setPanErr] = useState("");
  const toggleTag = (t) => setSelectedTags((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));

  useEffect(() => { api.get("/material-tags").then((r) => setTags(r.data)).catch(() => {}); }, [api]);

  const submit = async (e) => {
    e.preventDefault();
    if (selectedTags.length === 0) { toast.error("Please select at least one material category you supply."); return; }
    const pe = validatePan(form.pan);
    if (pe) { setPanErr(pe); toast.error(pe); return; }
    const ge = validateGstin(form.gst_number);
    if (ge) { setGstErr(ge); toast.error(ge); return; }
    const ce = crossCheckPanGstin(form.pan, form.gst_number);
    if (ce) { setPanErr(ce); toast.error(ce); return; }
    // Security Pulse gate — verify email ownership before creating the application.
    setPulseOpen(true);
  };

  const finishRegister = async (otp_code) => {
    setPulseOpen(false);
    setLoading(true);
    try {
      await registerSeller({
        ...form,
        gst_number: form.gst_number.trim().toUpperCase(),
        pan: form.pan.trim().toUpperCase(),
        material_tags: selectedTags,
        otp_code,
      });
      toast.success("Application submitted for review.");
      nav("/account?welcome=jitsi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RegisterLayout
      kicker="/ Seller Registration"
      title="Authorised Seller application"
      subtitle="Complete this critical profiling form to be reviewed for authorised-supplier status with Vinci Inc. Our trade desk verifies every applicant."
    >
      <Toaster position="bottom-right" theme="dark" richColors />

      <div className="flex items-center gap-3 rounded-sm border border-[#2FB999]/40 bg-[#2FB999]/5 px-5 py-4 mb-12 max-w-2xl">
        <ShieldCheck size={20} className="text-[#2FB999] shrink-0" />
        <p className="font-body text-sm text-[#C9C9C2]">Applications are reviewed by the Vinci trade desk. You'll be marked <span className="text-[#2FB999]">Authorised Seller</span> once approved.</p>
      </div>

      <form onSubmit={submit} data-testid="seller-form">
        <div className="grid md:grid-cols-2 gap-8">
          <Input label="Business / Company name" testid="seller-business-name" name="business_name" required value={form.business_name} onChange={onChange} />
          <Input label="Contact person" testid="seller-contact-person" name="contact_person" required value={form.contact_person} onChange={onChange} />
          <Input label="Email" testid="seller-email" name="email" type="email" required value={form.email} onChange={onChange} />
          <Input label="Phone" testid="seller-phone" name="phone" required value={form.phone} onChange={onChange} />
          <div>
            <Input label="GST number" testid="seller-gst" name="gst_number" required value={form.gst_number} onChange={onChange} onBlur={() => setGstErr(validateGstin(form.gst_number))} />
            {gstErr && <p data-testid="seller-gst-error" className="font-body text-xs text-[#C0392B] mt-2">{gstErr}</p>}
          </div>
          <div>
            <Input label="PAN" testid="seller-pan" name="pan" required value={form.pan} onChange={onChange} onBlur={() => setPanErr(validatePan(form.pan))} />
            {panErr && <p data-testid="seller-pan-error" className="font-body text-xs text-[#C0392B] mt-2">{panErr}</p>}
          </div>
          <Input label="Materials supplied" testid="seller-materials" name="materials_supplied" required placeholder="e.g. TMT bars, cement, structural steel" value={form.materials_supplied} onChange={onChange} />
          <Input label="Production / supply capacity" testid="seller-capacity" name="supply_capacity" required placeholder="e.g. 5,000 T / month" value={form.supply_capacity} onChange={onChange} />
          <Input label="Years in business" testid="seller-years" name="years_in_business" required value={form.years_in_business} onChange={onChange} />
          <Input label="Password (min 6 chars)" testid="seller-password" name="password" type="password" required minLength={6} value={form.password} onChange={onChange} />
          <Textarea label="Registered business address" testid="seller-address" name="address" required rows={2} value={form.address} onChange={onChange} className="md:col-span-2" />
          <Textarea label="Compliance / documents note (GST cert, licences, bank details on request)" testid="seller-docs" name="docs_note" rows={2} value={form.docs_note} onChange={onChange} className="md:col-span-2" />
          <div className="md:col-span-2" data-testid="seller-tags">
            <TagPicker
              label="Material categories you supply (used to match you to buyer requirements) *"
              tags={tags}
              selected={selectedTags}
              onToggle={toggleTag}
              testidPrefix="seller-tag"
            />
          </div>
        </div>
        <div className="mt-10">
          <SubmitButton testid="seller-submit" type="submit" disabled={loading}>
            {loading ? "Submitting…" : "Submit application"}
          </SubmitButton>
        </div>
      </form>
      <SecurityPulseGate
        open={pulseOpen}
        email={form.email}
        purpose="signup"
        displayName={form.contact_person || form.business_name}
        onClose={() => setPulseOpen(false)}
        onVerified={finishRegister}
      />
    </RegisterLayout>
  );
}
